import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type { GetCashProjectionInput, ListCashProjectionOrdersInput } from "@nexxt/contracts/sales";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the cash projection (spec v0.3 §9, C-55, ADR-261): the table in one read, the parts of orders behind
// a row one page at a time. Under the key of the sales orders, so every write on an order reads them again.

const projection = api.sales["cash-projection"];

export type CashProjection = InferResponseType<typeof projection.$get, 200>;

/** The filters of the table, which its rows keep. */
export type CashProjectionFilters = Pick<GetCashProjectionInput, "legalEntity" | "brand">;
/** What a row of the table opens: a period, or the parts left out for a reason. */
export type CashProjectionCell = Pick<ListCashProjectionOrdersInput, "bucket" | "excluded">;

const PAGE_SIZE = 50;

const keys = {
  all: ["sales-orders", "cash-projection"] as const,
};

export const cashProjectionQueries = {
  ...keys,

  get: (filters: CashProjectionFilters) =>
    queryOptions({
      queryKey: [...keys.all, "table", filters],
      queryFn: ({ signal }) => unwrap(projection.$get({ query: filterQuery(filters) }, { init: { signal } })),
    }),

  /** The soonest first. */
  orders: (filters: CashProjectionFilters, cell: CashProjectionCell) =>
    infiniteQueryOptions({
      queryKey: [...keys.all, "orders", filters, cell],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          projection.orders.$get(
            {
              query: {
                ...filterQuery({ ...filters, ...cell }),
                page: String(pageParam),
                pageSize: String(PAGE_SIZE),
              },
            },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),
};
